import React, { HTMLAttributes } from "react";
import { observer } from "mobx-react-lite";
import { useWalletVM } from "@components/Wallet/WalletModal/WalletVM";
import InvestRow from "@components/InvestRow";
import { Column } from "@components/Flex";
import SizedBox from "@components/SizedBox";
import Text from "@components/Text";
import Button from "@components/Button";
import { ReactComponent as NotFoundIcon } from "@src/assets/notFound.svg";
import styled from "@emotion/styled";
import { useStores } from "@stores";
import Skeleton from "react-loading-skeleton";
import BN from "@src/utils/BN";
import { Anchor } from "@components/Anchor";
import Balance from "@src/entities/Balance";

interface IProps extends HTMLAttributes<HTMLDivElement> {}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 400px;
`;

interface IBalanceWithDollarEquivalent extends Balance {
  stats?: any;
  dollarEquivalent: string;
}

const AssetsBalances: React.FC<IProps> = () => {
  const vm = useWalletVM();
  const { accountStore } = useStores();

  const [userAssets, setUserAssets] = React.useState(
    [] as IBalanceWithDollarEquivalent[]
  );
  React.useEffect(() => {
    const fetchUserAssets = async () => {
      const assets = vm.userAssets;
      const userAssets: IBalanceWithDollarEquivalent[] = await Promise.all(
        assets.map(async (b) => {
          const stats = await vm.tokenStats(b.assetId);
          const dollarEquivalent = new BN(b.formatBalance ?? 0)
            .times(stats?.prices?.min ?? BN.ZERO)
            .toFormat(2);

          return {
            ...b,
            logo: b.logo,
            stats,
            dollarEquivalent
          } as IBalanceWithDollarEquivalent;
        })
      );

      setUserAssets(userAssets);
    };

    fetchUserAssets();
  }, [vm]);

  if (accountStore.assetBalances === null)
    return (
      <Root style={{ padding: "0 24px" }}>
        <Skeleton height={56} style={{ marginBottom: 8 }} count={4} />
      </Root>
    );
  return (
    <Root>
      {userAssets.length !== 0 ? (
        userAssets.map((b) => {
          return (
            <InvestRow
              rateChange={BN.ZERO}
              key={b.assetId}
              logo={b.logo}
              topLeftInfo={b.name}
              topRightInfo={b.formatBalance}
              bottomLeftInfo={`$ ${b.stats?.prices.min}`}
              bottomRightInfo={b.formatUsdnEquivalent}
              withClickLogic
              onClick={() => {
                accountStore.setAssetToSend(b);
                accountStore.setSendAssetModalOpened(true);
              }}
            />
          );
        })
      ) : (
        <Column justifyContent="center" alignItems="center" crossAxisSize="max">
          <SizedBox height={16} />
          <NotFoundIcon />
          <Text type="secondary" size="medium" textAlign="center">
            You don’t have any assets on your wallet.
            <br />
            Buy WAVES on Waves Exchange to start trading.
          </Text>
          <SizedBox height={16} />
          <Anchor href="https://waves.exchange/trading/spot/WAVES_USDN">
            <Button size="medium">Buy WAVES</Button>
          </Anchor>
          <SizedBox height={100} />
        </Column>
      )}
    </Root>
  );
};
export default observer(AssetsBalances);
